<template>
  <div>
    <v-btn @click="dialog = true" color="warning" small> Edit Details </v-btn>
    <v-dialog v-model="dialog" persistent width="600">
      <v-card>
        <ModalHeader
          title="Edit Invoice Details"
          icon="mdi-cash"
          @close="dialog = false"
        />
        <v-card-text class="pa-4">
          <v-text-field
            outlined
            dense
            v-model="form.purpose"
            label="Purpose"
            prepend-icon="mdi-pen"
          ></v-text-field>
          <v-autocomplete
            outlined
            v-model="form.warranty_policy_id"
            :items="warranty_policies.filter((p) => p.is_active)"
            item-text="name"
            item-value="id"
            label="Warranty Policy *"
            prepend-icon="mdi-shield-check"
            :rules="[(v) => !!v || 'Warranty policy is required']"
            dense
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.name !== 'Regular Warranty'">
                  Major: {{ item.major_parts_warranty_value
                  }}{{ item.major_parts_warranty_unit.charAt(0) }} | Minor:
                  {{ item.minor_parts_warranty_value
                  }}{{ item.minor_parts_warranty_unit.charAt(0) }} | Service:
                  {{ item.service_warranty_value
                  }}{{ item.service_warranty_unit.charAt(0) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else>
                  Uses each item's own warranty duration
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <v-divider class="my-3"></v-divider>

          <!-- VAT Type Selection -->
          <v-radio-group
            v-model="form.vat_type"
            row
            @change="calculate_vat_based_on_type()"
            label="VAT Type"
          >
            <v-radio label="VAT Exclusive" value="exclusive"></v-radio>
            <v-radio label="VAT Inclusive (12%)" value="inclusive"></v-radio>
          </v-radio-group>

          <!-- Amount, Discount, VAT, Total -->
          <v-row>
            <v-col cols="6">
              <v-text-field
                label="Amount"
                prepend-inner-icon="mdi-cash"
                :value="form.amount"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                label="Discount"
                prepend-inner-icon="mdi-sale"
                v-model.number="form.discount"
                @input="calculate_discount()"
                type="number"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                label="Output VAT"
                prepend-inner-icon="mdi-cash-100"
                :value="form.vat"
                readonly
                type="number"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                label="Total Amount"
                prepend-inner-icon="mdi-cash-multiple"
                :value="form.total_amount"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <!-- SOP Section -->
          <v-radio-group
            v-model="form.has_sop"
            row
            @change="calculate_sop()"
            label="SOP"
          >
            <v-radio label="No SOP" :value="false"></v-radio>
            <v-radio label="With SOP" :value="true"></v-radio>
          </v-radio-group>

          <v-radio-group
            v-if="form.has_sop"
            v-model="form.sop_type"
            row
            @change="calculate_sop()"
            label="SOP Based On"
            class="mt-0"
          >
            <v-radio label="Percentage" value="percentage"></v-radio>
            <v-radio label="Fixed Amount" value="amount"></v-radio>
          </v-radio-group>

          <v-row v-if="form.has_sop">
            <v-col cols="6">
              <v-text-field
                label="SOP Percentage (%)"
                prepend-inner-icon="mdi-percent"
                v-model.number="form.sop_percentage"
                @input="calculate_sop()"
                :readonly="form.sop_type === 'amount'"
                :filled="form.sop_type === 'amount'"
                type="number"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                label="SOP Amount"
                prepend-inner-icon="mdi-cash"
                v-model.number="form.sop_amount"
                @input="calculate_sop()"
                :readonly="form.sop_type === 'percentage'"
                :filled="form.sop_type === 'percentage'"
                type="number"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                label="Cheque Amount"
                prepend-inner-icon="mdi-checkbook"
                :value="form.cheque_amount"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                label="VAT Amount (6%)"
                prepend-inner-icon="mdi-cash-100"
                :value="form.vat"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="submit" :loading="loading"
            >Submit</v-btn
          >
          <v-btn @click="dialog = false"> Close</v-btn>
          <v-spacer></v-spacer>
          <p class="caption grey--text">editInvoiceModal.vue</p>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  props: ["invoice"],
  data() {
    return {
      dialog: false,
      loading: false,
      form: {
        purpose: "",
        warranty_policy_id: null,
        amount: 0,
        discount: 0,
        vat: 0,
        vat_type: "exclusive",
        total_amount: 0,
        has_sop: false,
        sop_type: "percentage", // 'percentage' or 'amount'
        sop_percentage: 15,
        sop_amount: 0,
        cheque_amount: 0,
      },
    };
  },
  computed: {
    ...mapGetters({
      warranty_policies: "warranty_policy/warranty_policies",
    }),
    partial_amount() {
      return this.form.amount;
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.populateForm();
      }
    },
  },
  created() {
    if (this.warranty_policies.length <= 0) {
      this.$store.dispatch("warranty_policy/get_warranty_policies");
    }
  },
  methods: {
    populateForm() {
      this.form.purpose = this.invoice.purpose || "";
      this.form.warranty_policy_id = this.invoice.warranty_policy_id || null;
      this.form.amount = Number(this.invoice.amount) || 0;
      this.form.discount = Number(this.invoice.discount) || 0;
      this.form.vat_type = this.invoice.vat_type || "exclusive";
      this.form.has_sop = !!this.invoice.with_sop;
      this.form.sop_type = this.invoice.sop_type || "percentage";
      this.form.sop_percentage = Number(this.invoice.sop_percentage) || 15;
      this.form.sop_amount = Number(this.invoice.sop_amount) || 0;
      this.form.cheque_amount = Number(this.invoice.cheque_amount) || 0;
      this.form.vat = Number(this.invoice.vat) || 0;
      this.form.total_amount = Number(this.invoice.total_amount) || 0;

      // Recalculate to ensure consistency
      this.calculate_discount();
      this.calculate_vat_based_on_type();
      if (this.form.has_sop) {
        this.calculate_sop();
      }
    },
    calculate_vat_based_on_type() {
      if (this.form.vat_type === "inclusive") {
        const partial = this.partial_amount - this.form.discount;
        const vatable_amount = partial / 1.12;
        this.form.vat = Math.round(partial - vatable_amount);
      } else {
        this.form.vat = 0;
      }
      this.calculate_discount();
    },
    calculate_discount() {
      this.form.total_amount = this.partial_amount - this.form.discount;
      if (this.form.has_sop) {
        this.calculate_sop();
      }
    },
    calculate_sop() {
      if (!this.form.has_sop) {
        this.form.sop_amount = 0;
        this.form.sop_percentage = 0;
        this.form.cheque_amount = 0;
        return;
      }

      const vatable_amount = this.form.total_amount / 1.12;
      const total_vat = this.form.total_amount - vatable_amount;
      const six_percent_vat = total_vat / 2;
      const cheque_amount = vatable_amount + six_percent_vat;

      this.form.cheque_amount = Math.round(cheque_amount);
      this.form.vat = Math.round(six_percent_vat);

      if (this.form.sop_type === "amount") {
        // Fixed amount mode: user inputs the SOP amount directly.
        // Derive the equivalent percentage so backend recalculation stays
        // consistent (no DB schema change needed).
        this.form.sop_percentage =
          cheque_amount > 0
            ? Number(((this.form.sop_amount / cheque_amount) * 100).toFixed(2))
            : 0;
      } else {
        // Percentage mode: compute SOP amount from percentage of cheque amount
        this.form.sop_amount = Math.round(
          (cheque_amount * this.form.sop_percentage) / 100
        );
      }
    },
    async submit() {
      this.loading = true;
      let x = await this.$confirm({
        title: "Edit Invoice",
        message: "Are you sure you want to proceed?",
        color: "primary",
        icon: "mdi-help-circle-outline",
      });
      if (x) {
        const payload = {
          id: this.invoice.id,
          purpose: this.form.purpose,
          warranty_policy_id: this.form.warranty_policy_id,
          discount: this.form.discount,
          vat: this.form.vat,
          vat_type: this.form.vat_type,
          total_amount: this.form.total_amount,
          with_sop: this.form.has_sop,
          sop_percentage: this.form.has_sop ? this.form.sop_percentage : 0,
          sop_amount: this.form.has_sop ? this.form.sop_amount : 0,
          cheque_amount: this.form.has_sop ? this.form.cheque_amount : 0,
        };
        axios
          .put("update_invoice", payload)
          .then((res) => {
            // Update the invoice reference with all returned fields
            this.$set(
              this.invoice,
              "warranty_policy",
              res.data.warranty_policy
            );
            this.$set(this.invoice, "purpose", res.data.purpose);
            this.$set(
              this.invoice,
              "warranty_policy_id",
              res.data.warranty_policy_id
            );
            this.$set(this.invoice, "discount", res.data.discount);
            this.$set(this.invoice, "vat", res.data.vat);
            this.$set(this.invoice, "vat_type", res.data.vat_type);
            this.$set(this.invoice, "total_amount", res.data.total_amount);
            this.$set(this.invoice, "with_sop", res.data.with_sop);
            this.$set(this.invoice, "sop_percentage", res.data.sop_percentage);
            this.$set(this.invoice, "sop_amount", res.data.sop_amount);
            this.$set(this.invoice, "cheque_amount", res.data.cheque_amount);
            this.$alert({
              title: "Success",
              message: "Successfully updated invoice details.",
              color: "success",
              icon: "mdi-check-circle",
            });
            this.dialog = false;
          })
          .catch((err) => {
            this.$alert({
              title: "Error",
              message: String(err),
              color: "error",
              icon: "mdi-alert-circle",
            });
          });
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped></style>
